import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { AutocompleteChangeReason, AutocompleteInputChangeReason, Checkbox, Tooltip } from '@mui/material';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import axios from 'axios';

import { AdvertiserCriteria } from '../types/Criteria/AdvertiserCriteria';
import { Dealer as Advertiser, Dealer } from '../types/Dealer';
import InfiniteAutocomplete, { InfiniteAutocompleteProps } from './InfiniteAutocomplete';
import {
    AxiosInfinitePageableResponse,
    PAGEABLE_SIZE,
    PageableRequest,
    PageableResponse,
} from '../types/AxiosPageableResponse';
import Utils from './Utils';
import { useAutocompleteAutoSelectSingleOption } from '../hooks/Autocomplete';

/**
 * Advertiser Autocomplete Utilities
 */

const getOptionKey = (option: Advertiser | any): string | number => {
    return option?.id as number;
};

const getOptionLabel = (option: Advertiser | any): string => {
    return option?.dealerName || '';
};

/**
 * Advertiser Autocomplete Requests
 */

const defaultPageable: PageableRequest = {
    page: 1,
    size: PAGEABLE_SIZE,
    sort: 'asc',
    sortBy: 'dealerName',
};

const fetch = async (
    criteria: AdvertiserCriteria,
    { page = 1, ...pageable }: PageableRequest,
    signal?: AbortSignal
): Promise<PageableResponse<Advertiser>> => {
    const source = axios.CancelToken.source();

    if (signal) {
        if (signal.aborted) source.cancel('aborted');
        signal.addEventListener('abort', () => source.cancel('aborted'), { once: true });
    }

    const response = await axios.post('/api/advertiser/all', criteria, {
        headers: { XBusy: 'false' },
        params: {
            ...pageable,
            page: page - 1,
        },
        cancelToken: source.token,
    });

    return response.data;
};

const fetchInitial = async (ids: number[] | number | null | undefined, signal?: AbortSignal): Promise<Advertiser[]> => {
    const _ids: number[] = Array.isArray(ids) ? ids : ids != null ? [ids] : [];

    if (_ids.length === 0) {
        return [];
    }

    const pageSize = 1000;
    let page = 1;
    let allContent: Advertiser[] = [];
    let hasMore = true;

    while (hasMore) {
        const { content } = await fetch({ ids: _ids, activeStatus: undefined }, { page, size: pageSize }, signal);

        allContent = allContent.concat(content);
        hasMore = content.length === pageSize;
        page++;
    }

    return allContent;
};

const fetchInfinite = async (
    criteria: AdvertiserCriteria,
    { page = 1, ...pageable }: PageableRequest,
    signal?: AbortSignal
): Promise<AxiosInfinitePageableResponse<Advertiser>> => {
    const { content } = await fetch(criteria, { page, ...pageable }, signal);

    return {
        items: content,
        nextPage: content.length === pageable.size ? page + 1 : undefined,
    };
};

const fetchMass = async (criteria: AdvertiserCriteria, signal?: AbortSignal): Promise<Advertiser[]> => {
    const pageSize = 1000;
    let page = 1;
    let allContent: Advertiser[] = [];
    let hasMore = true;

    while (hasMore) {
        const { content } = await fetch(criteria, { page, size: pageSize }, signal);

        allContent = allContent.concat(content);

        hasMore = content.length === pageSize;
        page++;
    }

    return allContent;
};

/**
 * Advertiser Autocomplete
 */

interface AdvertiserAutocompleteProps<
    Multiple extends boolean | undefined = undefined,
    DisableClearable extends boolean | undefined = undefined,
    FreeSolo extends boolean | undefined = undefined
> extends Omit<
        InfiniteAutocompleteProps<Advertiser, Multiple, DisableClearable, FreeSolo>,
        'options' | 'value' | 'getOptionKey' | 'getOptionLabel' | 'renderOption'
    > {
    onLoading?: () => void;
    criteria?: AdvertiserCriteria;
    value: Multiple extends true ? number[] : number | null;

    autoSelectSingleOption?: boolean;
    deferChangeUntilClose?: boolean;

    // Status control prop
    showInactiveAdvertisersToggle?: boolean;
}

const AdvertiserAutocomplete = <
    Multiple extends boolean | undefined = undefined,
    DisableClearable extends boolean | undefined = undefined,
    FreeSolo extends boolean | undefined = undefined
>({
    onLoading = () => {},
    onChange,
    disabled = false,
    autoSelectSingleOption = false,
    deferChangeUntilClose = false,
    showInactiveAdvertisersToggle = false,
    ...props
}: AdvertiserAutocompleteProps<Multiple, DisableClearable, FreeSolo>) => {
    const { debounce } = Utils;

    const [options, setOptions] = useState<Advertiser[]>([]);
    const [value, setValue] = useState<
        InfiniteAutocompleteProps<Advertiser, Multiple, DisableClearable, FreeSolo>['value']
    >(
        (props.multiple ? [] : null) as InfiniteAutocompleteProps<
            Advertiser,
            Multiple,
            DisableClearable,
            FreeSolo
        >['value']
    );
    const [inputValue, setInputValue] = useState<string>('');
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [isMassSelected, setIsMassSelected] = useState<boolean>(false);
    const [hideInactive, setHideInactive] = useState<boolean>(true); // Default to hiding inactive when showInactiveStatus is enabled
    const pendingValueRef = useRef<
        InfiniteAutocompleteProps<Advertiser, Multiple, DisableClearable, FreeSolo>['value'] | null
    >(null);
    const hasChangedWhileOpenRef = useRef<boolean>(false);

    // Track user-selected IDs separately from criteria-filtered values
    // This ensures we can restore selections when other filters are cleared
    const [userSelectedIds, setUserSelectedIds] = useState<number[]>(
        Array.isArray(props.value) ? (props.value as number[]) : props.value != null ? [props.value as number] : []
    );
    const hasUserInteractedRef = useRef<boolean>(false);

    const criteriaWithoutName = useMemo((): AdvertiserCriteria => {
        return {
            oemIds: props?.criteria?.oemIds || [],
            oemProgramIds: props?.criteria?.oemProgramIds || [],
            agencyIds: props?.criteria?.agencyIds || [],
            agencyMarketRegions: props?.criteria?.agencyMarketRegions || [],
            // If showInactiveStatus is false, always filter to active only
            // If showInactiveStatus is true, respect the hideInactive toggle
            activeStatus: !showInactiveAdvertisersToggle ? true : hideInactive ? true : undefined,
        };
    }, [
        props.criteria?.agencyIds,
        props.criteria?.oemIds,
        props.criteria?.oemProgramIds,
        props?.criteria?.agencyMarketRegions,
        showInactiveAdvertisersToggle,
        hideInactive,
    ]);

    const criteria = useMemo((): AdvertiserCriteria => {
        return {
            ...criteriaWithoutName,
            name: inputValue,
        };
    }, [criteriaWithoutName, inputValue]);

    // Sync userSelectedIds with props.value on initial load (before user interaction)
    useEffect(() => {
        if (!hasUserInteractedRef.current) {
            const newIds: number[] = Array.isArray(props.value)
                ? (props.value as number[])
                : props.value != null
                ? [props.value as number]
                : [];
            setUserSelectedIds(newIds);
        }
    }, [props.value]);

    // Fetch initial value using userSelectedIds (preserves full user selections)
    const { data: initialValue, isLoading: isLoadingInitialValue } = useQuery({
        queryKey: ['infinite/advertisers/initial', userSelectedIds, criteriaWithoutName],
        queryFn: ({ signal }) => fetchInitial(userSelectedIds, signal),
        enabled: true,
        refetchOnWindowFocus: false,
    });

    // Filter displayed selections based on criteria from other filters
    // Calls onChange with filtered values so reporting query uses correct data
    // When criteria clears, all user selections are restored from userSelectedIds
    useLayoutEffect(() => {
        if (initialValue && initialValue.length > 0) {
            const filteredInitialValue: Advertiser[] = initialValue.filter((_value: Advertiser) => {
                if (criteria?.agencyIds && criteria.agencyIds.length > 0) {
                    if (!criteria.agencyIds.includes(_value.agencyId as number)) return false;
                }
                if (criteria?.oemProgramIds && criteria.oemProgramIds.length > 0) {
                    if (!_value.oemPrograms?.some((program) => criteria.oemProgramIds?.includes(program?.id as number)))
                        return false;
                }
                if (criteria?.oemIds && criteria.oemIds.length > 0) {
                    if (!_value.oems?.some((oem) => criteria.oemIds?.includes(oem?.oemId as number))) return false;
                }
                if (criteria?.agencyMarketRegions && criteria.agencyMarketRegions.length > 0) {
                    if (!criteria.agencyMarketRegions.includes(_value.agencyMarketRegion)) return false;
                }
                return true;
            });

            if (props.multiple) {
                setValue((filteredInitialValue || []) as any);

                if (JSON.stringify(filteredInitialValue) !== JSON.stringify(value)) {
                    onChange(filteredInitialValue as any);
                }
            } else {
                setValue((filteredInitialValue[0] || null) as any);

                if (filteredInitialValue[0]?.id !== (value as Advertiser)?.id) {
                    onChange(filteredInitialValue[0] as any);
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialValue, criteria, props.multiple]);

    const { data, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage, refetch } = useInfiniteQuery({
        queryKey: ['infinite/advertisers', criteria],
        queryFn: ({ pageParam, signal }) =>
            fetchInfinite(criteria, { page: pageParam || 1, size: defaultPageable.size }, signal),
        getNextPageParam: (lastPage) => lastPage.nextPage,
        initialPageParam: 0,
        enabled: true,
        refetchOnWindowFocus: false,
        placeholderData: (previousData) => previousData,
    });

    const { refetch: refetchMass, isLoading: isLoadingMass } = useQuery({
        queryKey: ['infinite/advertisers/mass', criteria],
        queryFn: ({ signal }) => fetchMass(criteria, signal),
        enabled: false,
        refetchOnWindowFocus: false,
    });

    const handleOnClose = useCallback((): void => {
        setIsOpen(false);
        if (deferChangeUntilClose && hasChangedWhileOpenRef.current && pendingValueRef.current !== null) {
            // Mark that user has interacted - don't sync from props.value anymore
            hasUserInteractedRef.current = true;
            // Store user's full selection for restoration when criteria clears
            const ids = Array.isArray(pendingValueRef.current)
                ? (pendingValueRef.current as Advertiser[]).map((v) => v.id as number)
                : pendingValueRef.current != null
                ? [(pendingValueRef.current as Advertiser).id as number]
                : [];
            setUserSelectedIds(ids);
            onChange(pendingValueRef.current as any);
        }
        hasChangedWhileOpenRef.current = false;
        pendingValueRef.current = null;
    }, [deferChangeUntilClose, onChange]);

    const handleOnChange = useCallback(
        (
            newValue: InfiniteAutocompleteProps<Advertiser, Multiple, DisableClearable, FreeSolo>['value'] | undefined,
            reason?: AutocompleteChangeReason
        ): void => {
            setValue(newValue);
            if (deferChangeUntilClose) {
                pendingValueRef.current = newValue ?? null;
                hasChangedWhileOpenRef.current = true;
            } else {
                onChange(newValue as any);
            }

            // If cleared via X button, trigger close logic immediately
            if (reason === 'clear') {
                setUserSelectedIds([]);
                handleOnClose();
            }
        },
        [deferChangeUntilClose, handleOnClose, onChange]
    );

    const debouncedOnInputChange = useRef(
        debounce((newInputValue: string): void => {
            refetch();
        }, 1000)
    ).current;

    const isLoading = useMemo((): boolean => {
        return isFetching || isFetchingNextPage || isLoadingInitialValue || isLoadingMass;
    }, [isFetching, isFetchingNextPage, isLoadingInitialValue, isLoadingMass]);

    const isSearching = inputValue.trim().length > 0;
    const isDisabled = disabled || (!isLoading && options.length === 0 && !isSearching);

    const handleOnInputChange = (
        event: React.SyntheticEvent,
        newInputValue: string,
        reason: AutocompleteInputChangeReason
    ): void => {
        setInputValue(newInputValue);
        debouncedOnInputChange(newInputValue);
    };

    const handleOnScroll = useCallback((): void => {
        if (!isMassSelected) {
            if (hasNextPage && !isFetchingNextPage) {
                fetchNextPage();
            }
        }
    }, [fetchNextPage, hasNextPage, isFetchingNextPage, isMassSelected]);

    useEffect((): void => {
        setOptions(data?.pages.flatMap((page) => page.items) || []);
    }, [data]);

    useEffect((): void => {
        if (isLoading) {
            onLoading();
        }
    }, [isLoading, onLoading]);

    useEffect(() => {
        if (!isOpen) {
            setInputValue('');
        }
    }, [isOpen]);

    useEffect(() => {
        setIsMassSelected(false);
    }, [criteria]);

    const handleOnFetchMass = async (): Promise<ReadonlyArray<Advertiser>> => {
        const result = await refetchMass();
        setIsMassSelected(true);
        return result.data ?? [];
    };

    const canAutoSelectSingleOption = !isLoading && !isSearching && !isMassSelected;

    useAutocompleteAutoSelectSingleOption<Dealer>({
        enabled: autoSelectSingleOption,
        condition: canAutoSelectSingleOption,
        options,
        value,
        multiple: !!props.multiple,
        getKey: (o) => o.id as number,
        onChange: (next) => {
            handleOnChange(next as any);
        },
    });

    return (
        <Tooltip
            arrow
            disableHoverListener={!isDisabled}
            title="The advertiser filter is disabled because there are no advertisers matching the current filter selections."
        >
            <InfiniteAutocomplete
                disableCloseOnSelect={props.multiple}
                openOnFocus={props.multiple}
                selectOnFocus={props.multiple}
                blurOnSelect={false}
                clearOnBlur={false}
                label="Advertiser"
                {...props}
                disabled={isDisabled}
                getOptionKey={getOptionKey}
                getOptionLabel={getOptionLabel}
                hideInactiveControl={
                    showInactiveAdvertisersToggle ? (
                        <div
                            onClick={() => setHideInactive(!hideInactive)}
                            style={{
                                cursor: 'pointer',
                                userSelect: 'none',
                                display: 'flex',
                                alignItems: 'center',
                            }}
                        >
                            <Checkbox checked={hideInactive} size="small" sx={{ pointerEvents: 'none' }} />
                            <span style={{ marginLeft: '0px' }}>Hide Inactive</span>
                        </div>
                    ) : undefined
                }
                renderOption={(liProps: any, option: Advertiser, state: { selected: boolean }): React.ReactNode => {
                    if (!option || !option.dealerName) {
                        return null;
                    }

                    const isActive = option?.activeStatus === true;

                    return (
                        <li
                            {...liProps}
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '8px 16px',
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
                                {props.multiple && (
                                    <Checkbox checked={state.selected} sx={{ marginRight: 1, flexShrink: 0 }} />
                                )}
                                <Tooltip title={option?.dealerName} placement="top" arrow>
                                    <span
                                        style={{
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {option?.dealerName}
                                    </span>
                                </Tooltip>
                            </div>
                            {showInactiveAdvertisersToggle && (
                                <span
                                    style={{
                                        fontSize: '12px',
                                        padding: '2px 8px',
                                        borderRadius: '12px',
                                        backgroundColor: isActive ? '#e8f5e9' : '#ffebee',
                                        color: isActive ? '#2e7d32' : '#c62828',
                                        fontWeight: 500,
                                        marginLeft: '8px',
                                        flexShrink: 0,
                                    }}
                                >
                                    {isActive ? 'Active' : 'Inactive'}
                                </span>
                            )}
                        </li>
                    );
                }}
                options={options}
                value={value}
                loading={isLoading}
                onChange={handleOnChange}
                inputValue={inputValue}
                onInputChange={handleOnInputChange}
                onScroll={handleOnScroll}
                onFetchMass={handleOnFetchMass}
                onOpen={(): void => setIsOpen(true)}
                onClose={handleOnClose}
            />
        </Tooltip>
    );
};

export type { AdvertiserAutocompleteProps };
export { defaultPageable as defaultAdvertiserPageable };
export default AdvertiserAutocomplete;
